import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';

import { DisplayRef, Namespace, SettingValue } from '@repo/sdk/models/components';

import { ScheduledAppKind } from '@/constants/scheduled-apps';
import {
  sequencerEditorConfigAtom,
  sequencerEditorReadingValuesAtom,
  sequencerEditorSettingValuesAtom,
} from '@/features/equipment-browser/equipment-details/state/sequencer-editor.state';
import { createUTCDate } from '@/helpers/datetime/utc-date';
import { useGetCompressorScheduleForecast } from '@/hooks/query-hooks/gaia-api/scheduled-apps';
import { useScheduledApps } from '@/hooks/query-hooks/scheduling';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { getLatestPrimitiveValue } from '@/types/reading-types';

import {
  getEnergyAIStageRows,
  hasUnmatchedEnergyAIRefs,
  isCompressorSequencingEnergyAIEnabled,
} from '../compressor-energy-ai.utils';

const getSettingPrimitiveValue = (value: SettingValue | undefined) => {
  return value?.number ?? value?.boolean ?? value?.enum ?? value?.string;
};

interface UseCompressorEnergyAIStageRowsProps {
  compressorOrder: string[];
}

export const useCompressorEnergyAIStageRows = ({
  compressorOrder,
}: UseCompressorEnergyAIStageRowsProps) => {
  const { agentId, organizationId } = useCurrentFacility();
  const config = useAtomValue(sequencerEditorConfigAtom);
  const readingValues = useAtomValue(sequencerEditorReadingValuesAtom);
  const settingValues = useAtomValue(sequencerEditorSettingValuesAtom);

  const isWiredForEnergyAI = !!config?.cosmicConditionExpression;
  const { data: scheduledApps } = useScheduledApps({
    agentId,
    enabled: isWiredForEnergyAI,
    kind: ScheduledAppKind.MpcCompressorSequencing,
    orgId: organizationId,
  });
  const hasEnergyAiSequence =
    isWiredForEnergyAI && isCompressorSequencingEnergyAIEnabled(scheduledApps);

  const {
    data: forecast,
    dataUpdatedAt,
    isError: isForecastError,
    isLoading,
  } = useGetCompressorScheduleForecast({
    agentId,
    enabled: hasEnergyAiSequence,
  });

  const readingValueById = useMemo(() => {
    return new Map(
      readingValues.map(({ id, value }) => [id, getLatestPrimitiveValue(value)] as const)
    );
  }, [readingValues]);

  const settingValueById = useMemo(() => {
    return new Map(
      settingValues.map(({ id, value }) => [id, getSettingPrimitiveValue(value)] as const)
    );
  }, [settingValues]);

  const readValue = useCallback(
    (displayRef: DisplayRef | undefined) => {
      const ref = displayRef?.ref;
      if (!ref?.id) {
        return undefined;
      }
      if (ref.namespace === Namespace.Setting) {
        return settingValueById.get(ref.id);
      }
      return readingValueById.get(ref.id);
    },
    [readingValueById, settingValueById]
  );

  // Anchored to the last successful fetch. Before the first one there is no
  // fetch timestamp, so it falls back to mount time - computed once rather than
  // per render, which would otherwise mint a new `now` and rebuild every row on
  // each render while the skeleton is showing.
  const now = useMemo(() => dataUpdatedAt || createUTCDate().getTime(), [dataUpdatedAt]);

  const rows = useMemo(() => {
    return getEnergyAIStageRows({
      compressorOrder,
      energyAICompressors: config?.energyAiCompressors,
      forecast,
      now,
      readValue,
    });
  }, [compressorOrder, config?.energyAiCompressors, forecast, now, readValue]);

  const hasForecastData = !!forecast && forecast.steps.length > 0;

  // The row-name to device-name join in findEnergyAIRefs crosses data sources and
  // fails silently: the stage fills keep rendering off the forecast, so a broken
  // join looks exactly like a facility whose compressors never ramp. Report the
  // unambiguous case - the BFF sent compressors and not one row matched - once per
  // mount, since nothing else would ever surface it.
  const hasReportedRefMismatch = useRef(false);
  useEffect(() => {
    if (hasReportedRefMismatch.current || !hasEnergyAiSequence) {
      return;
    }
    if (!config?.energyAiCompressors) {
      return;
    }
    if (!hasUnmatchedEnergyAIRefs({ energyAICompressors: config.energyAiCompressors, rows })) {
      return;
    }

    hasReportedRefMismatch.current = true;
    Sentry.captureMessage('Energy AI compressor refs matched no sequencer rows', {
      level: 'warning',
      extra: {
        agentId,
        energyAiCompressorNames: config.energyAiCompressors.map((refs) => refs.compressorName),
        energyAiDeviceAliases: config.energyAiCompressors.map((refs) => refs.deviceAlias),
        sequencerRowNames: compressorOrder,
      },
    });
  }, [agentId, compressorOrder, config?.energyAiCompressors, hasEnergyAiSequence, rows]);

  return {
    hasEnergyAiSequence,
    hasForecastData,
    isForecastError,
    isLoading,
    rows,
  };
};
